import { DEFAULT_VARIANT_NAME, DIRECT_CHANNEL } from 'shared/experiments/consts'
import { DEFAULT_EXPERIMENT } from 'shared/experiments/consts/experiments'
import type {
  ExperimentEvent,
  ExperimentName,
} from 'shared/experiments/consts/types'
import store from 'shared/store'
import noop from 'shared/utils/noop'
import type { Experiment } from 'types/shopConfig'
import { getExperimentsToActivate } from 'views/assets/scripts/optimizely'
import buildClientAPI from 'views/providers/clientAPI'
import { usePageProperties } from 'views/utils/shopAndRuntimeConfigUtils'

export const activateExperiment = (
  name: ExperimentName,
  withContentSquare?: boolean
) => {
  const { clientFockService } = buildClientAPI()

  if (withContentSquare) {
    // @ts-ignore
    const uxa = window._uxa || []
    uxa.push(['trackPageEvent', `${name}_activated`])
  }

  return clientFockService.activateExperiments([name])
}

/**
 * Takes the experiment id and returns true if it is enabled and rendered in DOM or listed in MANUALLY_ACTIVATED.
 */

export const isExperimentEnabled = (nameToFind: ExperimentName): boolean => {
  const experimentsToActivate = getExperimentsToActivate({
    includeExperimentsWithPreventActivation: true,
  })

  return experimentsToActivate.some(name => name === nameToFind)
}

export const isExperimentActive = (
  name: ExperimentName,
  experiments: Experiment[]
): boolean => {
  return experiments.some(
    experiment =>
      experiment.name === name && experiment.variant !== DEFAULT_VARIANT_NAME
  )
}

export const isAnyExperimentActive = (
  names: ExperimentName[],
  experiments: Experiment[]
): boolean =>
  experiments.some(
    ({ name, variant }) =>
      variant !== DEFAULT_VARIANT_NAME && names.includes(name)
  )

export const getExperiments = (
  names: ExperimentName[] = [],
  experiments: Experiment[] = []
): Experiment[] => experiments.filter(({ name }) => names.includes(name))

export const getExperiment = (
  name: ExperimentName,
  experiments: Experiment[] = []
): Experiment => {
  const experiment = experiments.find(
    ({ name: experimentName }) => experimentName === name
  )
  return experiment || ({ ...DEFAULT_EXPERIMENT, name } as Experiment)
}

export const getPricingStrategyExperimentValue = (
  experimentVariant: string
): string => {
  const customExpas: Record<string, string> = {
    default: DIRECT_CHANNEL,
    v1: 'tsta',
    v2: 'tstb',
    v3: 'tstc',
    v4: 'tstd',
  }

  if (!experimentVariant || !customExpas[experimentVariant]) {
    return DIRECT_CHANNEL
  }

  return customExpas[experimentVariant]
}

export const getExperimentOnClient = (
  experimentName: ExperimentName
): Experiment => {
  const {
    pageProperties: { experiments: experimentsList },
  } = store.getPublicRuntimeConfig()

  const experiment =
    experimentsList.find(({ name }) => name === experimentName) ||
    DEFAULT_EXPERIMENT

  return { ...experiment, name: experimentName } as Experiment
}
export const useGetExperiment = (
  experimentName: ExperimentName
): Experiment => {
  const { experiments: experimentsList = [] } = usePageProperties()

  const experiment =
    experimentsList.find(({ name }) => name === experimentName) ||
    DEFAULT_EXPERIMENT

  return { ...experiment, name: experimentName } as Experiment
}

/**
 * Binds an event handler that activates an experiment defined by the experiment name
 * to the elements that match the provided query selector.
 * Experiment must be enabled for this method to properly bind the event handler,
 * otherwise it's a noop.
 */
export const bindExperimentActivation = ({
  selector,
  event,
  experimentName,
}: ExperimentEvent): (() => void) => {
  if (!isExperimentEnabled(experimentName)) return noop

  // callback that removes all of the event listeners after the initial activation
  const removeEventListeners = (handlerRef: () => void) => {
    const elements = document.querySelectorAll(selector)
    if (elements.length > 0) {
      elements.forEach(element => {
        element.removeEventListener(event, handlerRef)
      })
    }
  }

  // a handler method
  const handler = () => {
    removeEventListeners(handler)
    activateExperiment(experimentName).catch(noop)
  }

  // bind event listeners
  const elements = document.querySelectorAll(selector)
  if (elements.length > 0) {
    elements.forEach(element => {
      element.addEventListener(event, handler)
    })
  }

  // return a method that removes the event listeners
  return () => removeEventListeners(handler)
}
